import { AfterViewInit, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { OpportunityUserView, SearchOpportunitiesMapParameters, SearchOpportunitiesMapResult } from '@api-proxies';
import { OpportunityService } from '@http/clients/opportunity.service';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { FilterStateService } from '@services/filter-state.service';
import { MapService, PanelFieldConfig } from '@services/map.service';
import { formatCurrencyUSD } from '@shared/grids/grid-formatters.config';
import { ICardDetails, MapCardComponent } from '@shared/map-card/map-card.component';
import { FilterValueOpportunities } from '@shared/opportunities-filters/opportunity-filters.config';
import { CounterBadgeComponent, DateTimeService, IconSanitizerService } from 'ngx-lib';
import { Subscription } from 'rxjs';

export interface IOpportunityCardDetails {
    id?: number;
    name?: string;
    contactInfo?: string;
    eventDate?: Date;
    code?: string;
    opportunityStatusName?: string;
    companyName?: string;
    opportunityValue?: number;
    eventType?: number;
    opportunityUsers?: OpportunityUserView[];
    latitude?: number;
    longitude?: number;
}

@Component({
    selector: 'app-opportunities-map',
    templateUrl: './opportunities-map.component.html',
    styleUrls: ['./opportunities-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterBadgeComponent, MapCardComponent]
})
export class OpportunitiesMapComponent implements AfterViewInit, OnDestroy {
    filters?: FilterValueOpportunities;
    opportunities: IOpportunityCardDetails[];
    searchConfig: SearchOpportunitiesMapParameters;
    selectedMarkerData: ICardDetails[];
    opportunitiesCounter?: number;
    closeIcon?: SafeHtml;
    panelConfig: PanelFieldConfig;

    private readonly subscriptions = new Subscription();

    constructor(
        private readonly awaiter: AwaiterService,
        private readonly opportunityService: OpportunityService,
        private readonly mapService: MapService,
        private readonly iconSanitizer: IconSanitizerService,
        private readonly filterStateService: FilterStateService<FilterValueOpportunities>,
        private readonly dateTimeService: DateTimeService
    ) {
        this.opportunities = [];
        this.selectedMarkerData = [];
        this.searchConfig = {} as SearchOpportunitiesMapParameters;
        this.closeIcon = this.iconSanitizer.getIcon('close');

        this.panelConfig = {
            mainInfo: [
                {
                    label: 'Due date',
                    valueFn: (opportunity: any) =>
                        opportunity.dueDate
                            ? this.dateTimeService.formatToString(
                                  this.dateTimeService.getDateTimeFromISOString(opportunity.dueDate),
                                  'MM/dd/yyyy'
                              )
                            : null
                },
                {
                    label: 'Opportunity Value',
                    valueFn: (opportunity: SearchOpportunitiesMapResult) =>
                        opportunity.potentialValue == null ? null : formatCurrencyUSD(opportunity.potentialValue)
                }
            ],
            tooltip: [
                { label: 'Customer', valueKey: 'companyName' },
                { label: 'Status', valueKey: 'opportunityStatusName' }
            ],
            routerLink: {
                base: '/opportunities-single',
                extraKeys: [TabNames[TabNames.Overview]]
            },
            salespersonsKey: 'opportunityUsers'
        };
        this.mapService.setPanelConfig(this.panelConfig);
    }

    async ngAfterViewInit(): Promise<void> {
        await this.mapService.initializeMap('opportunities');

        const markerSub = this.mapService.panelData$.subscribe(({ items, count }) => {
            this.selectedMarkerData = items;
            this.opportunitiesCounter = count;
        });

        const refreshSub = this.mapService.refresh$.subscribe(() => this.refreshMapView());

        const filterSub = this.filterStateService.stateFilters$.subscribe(async (newFilters) => {
            this.filters = { ...newFilters };

            this.closePanel();
            this.opportunities = [];

            this.refreshMapView();
        });

        this.subscriptions.add(refreshSub);
        this.subscriptions.add(markerSub);
        this.subscriptions.add(filterSub);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    closePanel(): void {
        this.selectedMarkerData = [];
    }

    async refreshMapView(): Promise<void> {
        this.buildSearchConfig();
        await this.awaiter.awaitAction('Loading Opportunities', async () => {
            this.opportunities = await this.opportunityService.searchMap(this.searchConfig);
            this.mapService.fetchData(this.opportunities, 'opportunities-data');
        });
    }

    private buildSearchConfig(): void {
        const bounds = this.mapService.getMapBounds();
        if (!bounds) return;

        this.searchConfig = {
            latitudeFrom: bounds.getSouth(),
            latitudeTo: bounds.getNorth(),
            longitudeFrom: bounds.getWest(),
            longitudeTo: bounds.getEast(),
            filterText: this.filters?.searchText ?? undefined,
            opportunityStatusIds: this.filters?.statuses?.length
                ? this.filters?.statuses.map((s: any) => s.id)
                : undefined,
            divisionIds: this.filters?.departments?.length
                ? this.filters?.departments.map((d: any) => d.id)
                : undefined,
            probabilityValueFrom: this.filters?.probabilityFrom,
            probabilityValueTo: this.filters?.probabilityTo,
            onlyFavorites: this.filters?.onlyFavorites,
            dueDateFrom: this.filters?.dueDateFrom,
            dueDateTo: this.filters?.dueDateTo,
            companyIds: this.filters?.companies?.length ? this.filters?.companies.map((c: any) => c.id) : undefined,
            salespersonIds: this.filters?.salespersons?.length
                ? this.filters?.salespersons.map((s: any) => s.externalId)
                : undefined,
            potentialValueFrom: this.filters?.potentialValueFrom,
            potentialValueTo: this.filters?.potentialValueTo,
            proposedGrossMarginFrom: this.filters?.proposedGrossMarginFrom,
            proposedGrossMarginTo: this.filters?.proposedGrossMarginTo,
            expectedResponseDateFrom: this.filters?.expectedResponseDateFrom,
            expectedResponseDateTo: this.filters?.expectedResponseDateTo,
            accountIds: this.filters?.accounts?.length ? this.filters?.accounts.map((a: any) => a.id) : undefined,
            companyRoleIds: this.filters?.companyRoles?.length
                ? this.filters?.companyRoles?.map((c: any) => c.id)
                : undefined,
            includeArchived: this.filters?.includeArchived ?? undefined
        } as SearchOpportunitiesMapParameters;
    }
}
